/* AURA HAVEN — LUXE behaviours
   1. Scroll reveals for .ah-reveal elements
   2. Free-shipping progress (header bar + cart drawer), threshold from data attribute
   3. Email popup timing (delay + exit intent + once per session)
   All progressive: the store works with this file missing. */
(() => {
  const root = document.documentElement;
  root.classList.add('ah-js');

  /* ---------- 1. Reveals ---------- */
  const revealInit = () => {
    const items = document.querySelectorAll('.ah-reveal:not(.is-in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* ---------- 2. Free shipping ---------- */
  const thresholdFor = () => {
    const el = document.querySelector('[data-ah-free-shipping-threshold]');
    const cents = el ? parseInt(el.getAttribute('data-ah-free-shipping-threshold'), 10) : NaN;
    return Number.isFinite(cents) && cents > 0 ? cents : 5000;
  };

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(root.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  let shippingTimer = null;
  const renderShipping = () => {
    if (shippingTimer) return;
    shippingTimer = setTimeout(async () => {
      shippingTimer = null;
      try {
        const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
        const subtotal = cart.items_subtotal_price || cart.total_price || 0;
        const threshold = thresholdFor();
        const remaining = Math.max(threshold - subtotal, 0);
        const pct = Math.min((subtotal / threshold) * 100, 100);
        const unlocked = remaining === 0;

        /* Header bar */
        const copy = document.querySelector('[data-ah-header-shipping-copy]');
        const fill = document.querySelector('[data-ah-header-shipping-fill]');
        if (copy && fill) {
          const idle = copy.getAttribute('data-idle-text') || copy.textContent;
          if (!copy.hasAttribute('data-idle-text')) copy.setAttribute('data-idle-text', idle);
          if (subtotal === 0) copy.textContent = idle;
          else if (unlocked) copy.textContent = copy.getAttribute('data-unlocked-text') || 'You’ve unlocked free shipping';
          else copy.textContent = (copy.getAttribute('data-remaining-text') || 'You’re [amount] away from free shipping').replace('[amount]', money(remaining));
          fill.style.width = pct + '%';
        }

        /* Cart drawer / cart page */
        let bar = document.querySelector('.ah-shipping-progress');
        if (!bar) {
          const anchor = document.querySelector('.cart-drawer__summary, .cart-drawer__footer, .cart__summary, .cart-summary, .cart-totals, .cart__footer, .cart__ctas');
          if (anchor) {
            bar = document.createElement('div');
            bar.className = 'ah-shipping-progress';
            bar.setAttribute('aria-live', 'polite');
            anchor.prepend(bar);
          }
        }
        if (bar) {
          bar.innerHTML =
            '<p class="ah-shipping-progress__text">' +
            (unlocked ? '<strong>You’ve unlocked free shipping.</strong>' : 'You’re <strong>' + money(remaining) + '</strong> away from free shipping.') +
            '</p><div class="ah-shipping-progress__bar"><div class="ah-shipping-progress__fill" style="width:' + pct + '%"></div></div>';
        }
      } catch (e) {
        /* silent */
      }
    }, 60);
  };

  /* Re-render when the theme's cart updates */
  ['cart:update', 'cart:change', 'cart:refresh', 'shopify:section:load', 'cart-drawer:open'].forEach((evt) => document.addEventListener(evt, renderShipping));
  /* Horizon fetches /cart/*.js; observe those calls too */
  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url;
      const p = origFetch.apply(this, arguments);
      if (url && /\/cart\/(add|change|update|clear)/.test(url)) p.then(() => setTimeout(renderShipping, 200)).catch(() => {});
      return p;
    };
  }
  const drawerObserver = new MutationObserver(() => renderShipping());
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component, .cart-drawer, #cart-drawer');
    if (drawer) drawerObserver.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
  };

  /* ---------- 3. Email popup ---------- */
  const popupInit = () => {
    const popup = document.querySelector('[data-ah-email-popup]');
    if (!popup) return;
    if (popup.dataset.ahReady === 'true') return;
    popup.dataset.ahReady = 'true';

    const key = 'auraHavenEmailPopupSession';
    const storeKey = 'auraHavenEmailPopupDismissedAt';
    const delay = Math.max(parseInt(popup.getAttribute('data-delay'), 10) || 12, 4) * 1000;
    const cooldownDays = parseInt(popup.getAttribute('data-cooldown-days'), 10) || 7;
    const inEditor = !!(window.Shopify && window.Shopify.designMode);
    const posted = popup.hasAttribute('data-posted');

    const dismissedRecently = () => {
      try {
        const t = parseInt(localStorage.getItem(storeKey), 10);
        return Number.isFinite(t) && Date.now() - t < cooldownDays * 864e5;
      } catch (e) {
        return false;
      }
    };

    const open = () => {
      if (popup.classList.contains('is-open')) return;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ah-popup-open');
      const input = popup.querySelector('input[type="email"]');
      if (input) setTimeout(() => input.focus({ preventScroll: true }), 350);
    };
    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ah-popup-open');
      try {
        sessionStorage.setItem(key, '1');
        localStorage.setItem(storeKey, String(Date.now()));
      } catch (e) {
        /* ignore */
      }
    };

    popup.querySelectorAll('[data-ah-popup-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
    });

    if (posted) {
      open();
      return;
    }
    if (inEditor) return; /* let the merchant preview via the section's preview toggle */
    let seen = false;
    try {
      seen = sessionStorage.getItem(key) === '1';
    } catch (e) {
      /* ignore */
    }
    if (seen || dismissedRecently()) return;
    if (/\/(cart|checkouts?|account)/.test(location.pathname)) return;

    const timer = setTimeout(open, delay);
    /* Exit intent on desktop */
    const onLeave = (e) => {
      if (e.clientY <= 0 && !popup.classList.contains('is-open')) {
        clearTimeout(timer);
        open();
        document.removeEventListener('mouseout', onLeave);
      }
    };
    if (window.matchMedia('(pointer: fine)').matches) document.addEventListener('mouseout', onLeave);
  };

  const init = () => {
    revealInit();
    renderShipping();
    watchDrawer();
    popupInit();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', () => {
    revealInit();
    popupInit();
  });
  /* Editor: open popup when its section is selected */
  document.addEventListener('shopify:section:select', (e) => {
    const popup = e.target && e.target.querySelector && e.target.querySelector('[data-ah-email-popup]');
    if (popup) {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
    }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const popup = e.target && e.target.querySelector && e.target.querySelector('[data-ah-email-popup]');
    if (popup) {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
    }
  });
})();
